import { reactive } from 'vue'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import min from 'lodash/min'
import max from 'lodash/max'
import chunk from 'lodash/chunk'
import sortBy from 'lodash/sortBy'
import { bbox, bboxPolygon, booleanIntersects } from '@turf/turf'
import { httpGetJson, httpPostJson, retry } from '@/lib/net'
import httpClient from '@/lib/http-client'
import {
  TIME_TO_MARKUPS_LOAD,
  TIME_TO_MARKUPS_LOAD_START,
  TIME_TO_MARKUPS_LOAD_STOP,
} from '@/lib/performance-marks'
import split from '@/lib/split'

import { log } from '@/services/logging-service'
import {
  applyFilters,
  checkForExistingFilters,
  checkFilterValidity,
  setMarkupLocations,
  getSearchTermFilter,
  getHasPhotosFilter,
  getContentOwnersFilter,
  getWaypointTypeFilter,
  getWaypointColorFilter,
  clearFilters,
  getSharedWithFilter,
} from '@/services/filter-markup-service'

import contentCollectionService from '@/services/content-collection-service'
import { deleteRoutes } from '@/services/route-service'
import { getUserId } from '@/services/identity-client'
import { getProfile } from '@/services/profile-service'
import { v4 as uuid } from 'uuid'
import { generateMarkupGeoJson } from '@/lib/markup-geojson'
import markupRenderService from '@/services/markup-service/render'
import {
  clearStoredVisibility,
  getStoredVisibility,
  isStoredVisibilityLoaded,
  loadStoredVisibility,
  saveStoredVisibility,
  setStoredVisibility,
} from '@/services/markup-service/visibility-state'
import {
  mapReadyPromise,
  visibleRegion,
  isInView,
} from '@/services/map-service'
import { removeShareGrantForEntity } from '@/services/content-sharing-service'
import { panToMarkup } from '@/lib/feature-utils'
import type Geographic from '@/lib/geographic'
import Color from '@/lib/color'
import markup from '@/lib/markup'
import {
  getRgbaColor,
  getRgbaFillColor,
  type MarkupColor,
  type ShapeFillColor,
} from '@/lib/markup-colors'
import router from '@/router'

import Paywall from '@/components/paywall'
import {
  FOCUSED_MARKUP_MODES,
  DEFAULT_MARKUP_NAME_REGEX,
  MARKUP_TYPE_LABELS,
  MARKUP_TYPE_LABELS_PLURAL,
  MARKUP_TYPE_ORDER_BY_APP,
  FOCUSED_MARKUP_EDIT_MODES,
  FOCUSED_MARKUP_VIEW_MODES,
} from '@/services/markup-service/constants'
import type {
  ContentProvider,
  PackageContentProvider,
} from '@/services/markup-service/types'
import type {
  CreateUpdateLineInput,
  CreateUpdateTrackInput,
  CreateUpdateShapeInput,
  CreateRouteInput,
  UpdateRouteInput as PackageUpdateRouteInput,
  CreateUpdateWaypointInput,
  RGBA,
  RawWaypoint,
  RawShape,
  RawLine,
  RawTrack,
  RawRoute as PackageRawRoute,
} from '@onxmaps/markups-web'
import {
  createShapesProvider,
  createRoutesProvider,
  createLinesProvider,
  createTracksProvider,
  createWaypointsProvider,
  Waypoint,
  Shape,
  Line,
  Track,
  Route,
} from '@onxmaps/markups-web'

import marketingEventService from '@/services/marketing-event-service'
import waypointImageService from '@/services/waypoint-image-service'
import { getPhotoById } from '@/services/photo-service'
import type WaypointImage from '@/lib/waypoint-image'
import {
  getSortTypeEventValue,
  getFilterTypeListValue,
  getContentTypeListOrigin,
} from '@/lib/content-events'
import type { EventProps } from '@/lib/marketing'
import type MapFeature from '@/lib/map-feature'
import type {
  GeoJSON,
  Feature,
  MultiPolygon,
  Polygon,
  Point,
  LineString,
  Position,
} from 'geojson'

import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type {
  RawMarkup,
  Markup,
  CreateUpdateMarkupInput,
  MarkupPhoto,
  RawRoute,
  UpdateRouteInput,
  PackageCreateUpdateMarkupInput,
} from '@/types/markups'

import {
  isWaypoint,
  isLine,
  isTrack,
  isShape,
  isRoute,
} from '@/services/markup-service/helpers'
import { MarkupType } from '@/types/markups'
import { isActive } from '@/services/feature-flag-service'

import {
  publishLineCreated,
  publishLineDeleted,
  publishLineUpdated,
  publishTrackCreated,
  publishTrackDeleted,
  publishTrackUpdated,
  publishRouteDeleted,
  publishRouteSaved,
  publishRouteUpdated,
  publishShapeCreated,
  publishShapeDeleted,
  publishShapeUpdated,
  publishWaypointCreated,
  publishWaypointDeleted,
  publishWaypointUpdated,
} from '@/services/markup-service/markup-events'

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
export let readyResolver: () => void | null
export const markupsLoaded = new Promise<void>(
  (resolve) => (readyResolver = resolve)
)

export interface MarkupResult {
  success: boolean
  result?: Markup[]
  reason?: string
  query?: string
  property?: unknown
}

interface FocusedMarkup {
  markup: Markup | null
  mode: string
  hasUnsavedChanges: boolean
}

interface ChunkedDeleteResult {
  deletedIds: string[]
  error?: unknown
}

const DELETE_CHUNK_SIZE = 999

/**
 * Send ids to a bulk delete endpoint in chunks, one request at a time. Parallel
 * requests collide on the backend's per-account bulk delete lock, so each chunk
 * waits for the previous response. Stops at the first failed chunk.
 */
const deleteInChunks = async (
  ids: string[],
  deleteChunk: (chunkIds: string[]) => Promise<unknown>
): Promise<ChunkedDeleteResult> => {
  const deletedIds: string[] = []

  for (const chunkIds of chunk(ids, DELETE_CHUNK_SIZE)) {
    try {
      await deleteChunk(chunkIds)
    } catch (error) {
      return { deletedIds, error }
    }
    deletedIds.push(...chunkIds)
  }

  return { deletedIds }
}

/**
 * Service for requesting, modifying, storing the state of markups
 */
class MarkupService {
  /**
   * Initialization
   */
  private _focusedMarkup: FocusedMarkup = {
    markup: null,
    mode: '',
    hasUnsavedChanges: false,
  }
  private _highlightedMarkups: Set<string> = new Set()
  private _visibleMarkups: {
    [key: string]: boolean
  } = {}
  private _currentUserId: string = ''
  private _userIdPromise: Promise<string> | null = null

  // FUTURE need this for knowing when the mapViewer.markups has been loaded, this can go away once we no longer need mapviewer.markups
  mapMarkupInitialized: boolean = false
  isFetching: boolean = false

  private _queryMarkupFeatures: MapFeature[] | null = null
  private _queryMarkupResult: MarkupResult | null = null
  private _queryMarkupLoading: boolean = false
  private _queryMarkupTruncated: boolean = false

  constructor() {
    // Non-route providers only need httpClient + fixed colors, both available
    // synchronously, so create them eagerly. This keeps getMarkupByID and the
    // synchronous getters safe even before Split resolves.
    const linesDefaultColor = getRgbaColor(
      markup.getColor('orange') as MarkupColor
    )
    this._linesProvider = createLinesProvider({
      httpClient,
      defaultColor: linesDefaultColor,
    })

    const tracksDefaultColor = getRgbaColor(
      markup.getColor('cyan') as MarkupColor
    )
    this._tracksProvider = createTracksProvider({
      httpClient,
      defaultColor: tracksDefaultColor,
    })

    const shapesDefaultColor = getRgbaColor(
      markup.getColor('orange') as MarkupColor
    )
    const shapesDefaultFillColor = getRgbaFillColor(
      markup.getFillColor('blue') as ShapeFillColor
    )
    this._shapesProvider = createShapesProvider({
      httpClient,
      defaultColor: shapesDefaultColor,
      defaultFillColor: shapesDefaultFillColor,
    })

    const waypointsDefaultColor = getRgbaColor(
      markup.getWaypointDefaultColor() as MarkupColor
    )
    this._waypointsProvider = createWaypointsProvider({
      httpClient,
      defaultColor: waypointsDefaultColor,
    })

    // Routes need the user ID, so initialize the routes provider once Split
    // (and then identity) is ready. It stays undefined on pre-auth pages
    // (i.e. share-receive) where userId is unavailable; routes reads guard
    // against that.
    split.splitClientsReady().then(() => {
      // Promise.resolve guards the chain: shared/jest.setup.js auto-mocks
      // @/services/identity-client repo-wide, so getUserId() is undefined under test.
      this._userIdPromise = Promise.resolve(getUserId()).then((id) => {
        this._currentUserId = id ?? ''
        if (this._currentUserId) {
          const defaultColor = getRgbaColor(
            markup.getColor('black') as MarkupColor
          )
          this._routesProvider = createRoutesProvider({
            httpClient,
            userId: this._currentUserId,
            defaultColor,
          })
        }
        return this._currentUserId
      })
    })
  }

  /**
   * Returns a promise that resolves when user ID is available. The user ID fetch
   * is started in the constructor once Split is ready; if it hasn't started yet,
   * resolve with the current (possibly empty) user ID rather than blocking.
   */
  private _ensureUserId(): Promise<string> {
    return this._userIdPromise ?? Promise.resolve(this._currentUserId)
  }

  // Non-route providers only need httpClient + fixed (non-vertical) colors, both
  // available synchronously, so they are created eagerly in the constructor. The
  // routes provider needs the user ID and a vertical-specific color, so it is
  // created asynchronously once Split resolves and stays undefined until then
  // (e.g. pre-auth share-receive); routes reads guard against that.
  private _linesProvider: ReturnType<typeof createLinesProvider>
  private _tracksProvider: ReturnType<typeof createTracksProvider>
  private _shapesProvider: ReturnType<typeof createShapesProvider>
  private _waypointsProvider: ReturnType<typeof createWaypointsProvider>
  private _routesProvider: ReturnType<typeof createRoutesProvider> | undefined

  /**
   * Returns features (e.g., property boundaries) used for performing a markup query.
   *
   * queryMarkupFeatures - Array of features
   */
  queryMarkupFeatures() {
    return this._queryMarkupFeatures
  }

  /**
   * Sets features (e.g., property boundaries) used for performing a markup query.
   *
   * featureArr - Array of features
   */
  setQueryMarkupFeatures(featureArr: MapFeature[]) {
    this._queryMarkupFeatures = featureArr
    // make sure query markup result is getting reset whenever query markup
    // features is set or reset
    this.setQueryMarkupResult({ success: false })
  }

  /**
   * Returns result of feature query against queryMarkupFeatures
   *
   * queryMarkupResult - Query result
   */
  queryMarkupResult() {
    return this._queryMarkupResult
  }

  /**
   * Sets the markup query result
   *
   * result - Markup result
   */
  setQueryMarkupResult(result: MarkupResult) {
    this._queryMarkupResult = result
  }

  /**
   * Returns whether markup query is currently running
   */
  queryMarkupLoading() {
    return this._queryMarkupLoading
  }

  /**
   * Sets whether the markup query is currently running
   */
  setQueryMarkupLoading(loading: boolean) {
    this._queryMarkupLoading = loading
  }

  /**
   * Returns whether the markup query may be incomplete because the query region is partly outside the viewport
   */
  queryMarkupTruncated() {
    return this._queryMarkupTruncated
  }

  /**
   * Sets whether the markup query may be truncated
   */
  setQueryMarkupTruncated(truncated: boolean) {
    this._queryMarkupTruncated = truncated
  }

  /**
   * Request all markups during startup initialization.
   * This path performs a full refresh and updates visibility state.
   */
  async initialRequestAll() {
    // Wait for Split to load to ensure providers are initialized (see constructor).
    await split.splitClientsReady()

    await this.requestAllMarkups()
    await this.updateVisibility()

    this.mapMarkupInitialized = true
    readyResolver?.()
  }

  async initialRequestBbox() {
    if (!this.isFetching) {
      await mapReadyPromise
      await this.requestBboxMarkups()
    }
  }

  /**
   * Refresh markups from the backend
   */
  async refreshMarkups() {
    // Wait for Split to load to ensure providers are initialized (see constructor).
    await split.splitClientsReady()

    await this.requestAllMarkups(false)
    await this.updateVisibility()
  }

  /**
   * Requests all markups.
   *
   * logTotalPageLoad - Whether to log initial page load to markup load complete timing.
   */
  async requestAllMarkups(logTotalPageLoad = true) {
    this.isFetching = true

    performance.mark(TIME_TO_MARKUPS_LOAD_START)

    // Only package route provider needs userId.
    // Let other providers begin loading right away so markups aren't blocked
    // while we wait for identity, then resolve routes once userId is ready.
    const routeLoadPromise = this._ensureUserId().then(() => {
      return this._routesProvider
        ? this.loadContent(this._routesProvider)
        : undefined
    })

    await Promise.allSettled([
      this.loadContent(this._linesProvider),
      this.loadContent(this._tracksProvider),
      this.loadContent(this._waypointsProvider),
      this.loadContent(this._shapesProvider),
      routeLoadPromise,
    ])

    performance.mark(TIME_TO_MARKUPS_LOAD_STOP)
    performance.measure(
      TIME_TO_MARKUPS_LOAD,
      TIME_TO_MARKUPS_LOAD_START,
      TIME_TO_MARKUPS_LOAD_STOP
    )

    performance.mark('time_for_markup_and_route_load_finished')

    if (logTotalPageLoad) {
      performance.measure(
        'time_from_page_load_to_markup_load',
        undefined,
        'time_for_markup_and_route_load_finished'
      )
    }
    this.isFetching = false

    this._logMarkupDistribution()
  }

  async requestBboxMarkups() {
    performance.mark('start_visible_region_retrieve')
    const region = bbox(visibleRegion()).map(
      (b) => Math.trunc(b * 100_000) / 100_000
    )
    performance.mark('end_visible_region_retrieve')
    performance.measure(
      'visible_region_retrieve',
      'start_visible_region_retrieve',
      'end_visible_region_retrieve'
    )

    this.isFetching = true
    await Promise.allSettled([
      this.loadContentFromBbox(this._tracksProvider, region),
      this.loadContentFromBbox(this._linesProvider, region),
      this.loadContentFromBbox(this._waypointsProvider, region),
      this.loadContentFromBbox(this._shapesProvider, region),
    ])
    this.isFetching = false
  }

  /**
   * Get the waypoint list
   */
  getWaypoints(): Waypoint[] {
    return this._waypointsProvider.get()
  }

  /**
   * Get the shape list
   */
  getShapes(includeGeo = false): Array<Shape> {
    const shapes = !includeGeo
      ? this._shapesProvider.getWithoutGeo()
      : this._shapesProvider.get()

    return shapes
  }

  /**
   * Get the lines list
   * @param includeGeo - Whether to include the geojson or not
   */
  getLines(includeGeo = false): Array<Line> {
    return !includeGeo
      ? this._linesProvider.getWithoutGeo()
      : this._linesProvider.get()
  }

  /**
   * Get the tracks list
   * @param includeGeo - Whether to include the geojson or not
   */
  getTracks(includeGeo = false): Array<Track> {
    return !includeGeo
      ? this._tracksProvider.getWithoutGeo()
      : this._tracksProvider.get()
  }

  /**
   * Load content using the passed in provider and perform post load actions
   *
   * contentProvider - Content Provider to use for loading
   */

  async loadContent<
    T extends Markup,
    U extends CreateUpdateMarkupInput | RawRoute,
    V extends
      | CreateUpdateMarkupInput
      | { uuid: string; data: UpdateRouteInput },
  >(contentProvider: ContentProvider<T, U, V> | PackageContentProvider) {
    const contentItems = await contentProvider.load()

    // Generate images for waypoints after loading
    if (Object.is(contentProvider, this._waypointsProvider)) {
      const waypoints = contentItems as unknown as Waypoint[]

      const imagePromises = waypoints.map((wp) => {
        return this.generateWaypointImages(wp)
      })

      await Promise.all(imagePromises)
    }

    this._initVisibilityStateFromStorage(contentItems)
    setMarkupLocations(contentItems)
    markupRenderService.addMarkups(contentItems)
  }

  async loadContentFromBbox<
    T extends Markup,
    U extends CreateUpdateMarkupInput | RawRoute,
    V extends
      | CreateUpdateMarkupInput
      | { uuid: string; data: UpdateRouteInput },
  >(
    contentProvider: ContentProvider<T, U, V> | PackageContentProvider,
    bounds: number[]
  ) {
    if (isActive('fox_viewport_markup_load')) {
      const contentItems = contentProvider.loadBbox
        ? await contentProvider?.loadBbox(bounds)
        : []

      // Generate images for waypoints after loading from bbox
      if (
        Object.is(contentProvider, this._waypointsProvider) &&
        contentItems.length > 0
      ) {
        const waypoints = contentItems as unknown as Waypoint[]

        const imagePromises = waypoints.map((wp) =>
          this.generateWaypointImages(wp)
        )

        await Promise.all(imagePromises)
      }

      this._initVisibilityStateFromStorage(contentItems)
      setMarkupLocations(contentItems)
      markupRenderService.addMarkups(contentItems)
    }
  }

  /**
   * Get markups inside a share by its ID. This is a legacy "sharing" call that
   * goes through the markup service. We may eventually migrate this to a call on
   * the sharing service directly.
   *
   * id - ID of the share we would like to retrieve from the service
   * autoAccept - Default true, whether or not to add the markups to the users content
   * returns - List of shared markups
   */
  async getSharedMarkupsByShareID(id: string, autoAccept: boolean = true) {
    try {
      let sharedMarkups
      if (autoAccept) {
        sharedMarkups = await httpGetJson<RawMarkup[]>(
          `/v1/markups?share_id=${id}`
        )
      } else {
        sharedMarkups = await httpGetJson<RawMarkup[]>(
          `/v1/markups/shared/${id}`
        )
      }

      if (!sharedMarkups) {
        return []
      }

      const alreadyInList = (m: Markup) => !this.getMarkupByID(m.uuid)
      // filter out any markups from the share that are already in the list
      // Routes are handled separately in getSharedRoutesByShareID.
      const vmMarkups = sharedMarkups
        .map((raw) => {
          if (raw.type === MarkupType.waypoint) {
            return new Waypoint({ raw: raw as RawWaypoint })
          }
          if (raw.type === MarkupType.shape) {
            return new Shape({ raw: raw as RawShape })
          }
          if (raw.type === MarkupType.line) {
            return new Line({ raw: raw as RawLine })
          }
          if (raw.type === MarkupType.track) {
            return new Track({ raw: raw as RawTrack })
          }
          // Routes (and any unhandled types) map to a package Route view model.
          return new Route({
            raw: raw as unknown as PackageRawRoute,
          })
        })
        .filter(Boolean) as Array<Markup>

      if (autoAccept) {
        const waypointsToProcess: Waypoint[] = []

        vmMarkups.forEach((m) => {
          if (m.type === 'route') {
            return
          }

          if (alreadyInList(m)) {
            if (isLine(m as Line)) {
              this._linesProvider.add(m as Line)
            } else if (isTrack(m as Track)) {
              this._tracksProvider.add(m as Track)
            } else if (isWaypoint(m as Waypoint)) {
              this._waypointsProvider.add(m as Waypoint)
              waypointsToProcess.push(m as Waypoint)
            } else if (isShape(m as Shape)) {
              this._shapesProvider.add(m as Shape)
            }
            // make sure each new markup is set as visible
            markupRenderService.addMarkup(m, true)
            if (m.uuid) {
              this.setVisibility(m.uuid, true)
            }
          }
        })

        // Generate images for all shared waypoints that were added
        const validWaypoints = waypointsToProcess as Waypoint[]

        const imagePromises = validWaypoints.map((wp) =>
          this.generateWaypointImages(wp)
        )
        await Promise.all(imagePromises)
      }

      // return the full list so the share accept card can be shown, even if already accepted
      return vmMarkups
    } catch (e) {
      log('get_shared_markups_by_share_id_failed', {
        error: e,
        share_id: id,
      })
      return
    }
  }

  /**
   * Get routes inside a share by its ID.
   *
   * id - ID of the share we would like to retrieve from the service
   * returns - List of shared routes
   */
  async getSharedRoutesByShareID(
    id: string,
    withRetry: boolean,
    autoAccept: boolean = true
  ) {
    try {
      const get = () => httpGetJson(`/v1/routing/routes/shared/${id}`)
      const sharedRoutes = (
        withRetry ? await retry(get, 10, 500) : await get()
      ) as { data: RawRoute[] }

      const sharedRouteMarkups: Route[] = []
      const currentUserId = await getUserId()

      for await (const m of sharedRoutes.data) {
        const routeMarkupToAdd = new Route({
          raw: m as unknown as PackageRawRoute,
        })

        const isShared = Boolean(
          routeMarkupToAdd.createdBy &&
          routeMarkupToAdd.createdBy !== currentUserId
        )

        routeMarkupToAdd.isShared = isShared

        if (isShared && routeMarkupToAdd.createdBy) {
          const profile = await getProfile(routeMarkupToAdd.createdBy)
          // owner is assigned the legacy PublicProfile shape (unchanged runtime behavior)
          ;(routeMarkupToAdd as unknown as { owner?: unknown }).owner = profile
        }

        if (autoAccept) {
          this.setVisibility(routeMarkupToAdd.uuid, true)
          this._routesProvider?.add(routeMarkupToAdd as Route)
        }
        sharedRouteMarkups.push(routeMarkupToAdd)
      }

      if (autoAccept) {
        await markupRenderService.addMarkups(sharedRouteMarkups)
        setMarkupLocations(sharedRouteMarkups)
      }

      return sharedRouteMarkups
    } catch (e) {
      log('get_shared_routes_by_share_id_failed', {
        error: e,
        share_id: id,
      })
      return
    }
  }

  /**
   * Get all markups in a single list
   * @param includeGeo - Whether to include the geojson or not
   *
   * returns - all markups
   */
  getAll(includeGeo = false) {
    const markups = [
      ...this.getWaypoints(),
      ...this.getTracks(includeGeo),
      ...this.getShapes(includeGeo),
      ...this.getLines(includeGeo),
      ...this.getRoutes(includeGeo),
    ]

    return markups
  }

  /**
   * Returns all markups of a given type.
   *
   * type - Markup type
   */
  getMarkupsByType(type: MarkupType | 'all') {
    switch (type) {
      case 'all':
        return this.getAll()
      case 'waypoint':
        return this.getWaypoints()
      case 'track':
        return this.getTracks()
      case 'shape':
        return this.getShapes()
      case 'line':
        return this.getLines()
      case 'route':
        return this.getRoutes()
      default:
        throw new Error(`Invalid markup type: "${type}"`)
    }
  }

  /**
   * Gets all visible markups
   *
   * returns - all visible markups.
   */
  getAllVisible() {
    return this.getAll().filter((m) => {
      if ('uuid' in m && m.uuid) {
        return this._visibleMarkups[m.uuid] ?? false
      }
      return false
    })
  }

  /**
   * Gets all hidden markups
   *
   * returns - all hidden markups.
   */
  getAllHidden() {
    return this.getAll().filter((m) => {
      return !this._visibleMarkups[m.uuid]
    })
  }

  /**
   * Returns true if all markups of the given type are hidden.
   *
   * type - Markup type
   * returns - True if all markups of the given type are hidden. Returns false if
   * there are no markups of the given type.
   */
  isMarkupTypeHidden(type: MarkupType) {
    const markups = this.getMarkupsByType(type)
    if (!markups?.length) {
      return false
    }
    for (const markup of markups) {
      if (!('uuid' in markup) || !markup.uuid) {
        return false
      }
      const visible = this._visibleMarkups[markup.uuid]
      if (visible) {
        return false
      }
    }
    return true
  }

  /**
   * Returns the amount of markups hidden for a given type.
   *
   * type - Markup type
   * returns - Amount of markups of a given type that are hidden
   */
  getMarkupTypeHiddenCount(type: MarkupType) {
    const markups = this.getMarkupsByType(type)
    let amountHidden = 0

    if (!markups?.length) {
      return amountHidden
    }
    for (const markup of markups) {
      if (!('uuid' in markup) || !markup.uuid) {
        continue
      }
      const visible = this._visibleMarkups[markup.uuid]
      if (!visible) {
        amountHidden++
      }
    }
    return amountHidden
  }

  /**
   * Toggles visibility of all markups of a given type.
   *
   * type - Markup type
   * visible -  If specified, the visibility state will be updated
   *    accordingly (true to show all, false to hide all). If unspecified, the visibility will be toggled based on
   *    current value.
   * resetFilters - Whether to clear markup filters when enabling visibility. Clearing filters ensures that bulk
   * showing markups supersedes any filtering done in My Content.
   */
  toggleMarkupType(type: MarkupType, visible: boolean, resetFilters: boolean) {
    const markups = this.getMarkupsByType(type)
    const markupIds = markups.map((markup) => markup.uuid)
    visible = visible === undefined ? this.isMarkupTypeHidden(type) : visible
    if (visible && resetFilters) {
      clearFilters()
      this.resetVisibilityToStoredState()
    }
    this.bulkSetVisibility(markupIds, visible, true)
  }

  /**
   * Get the total markup count
   *
   * returns - total markup count
   */
  getTotalMarkupCount() {
    return this.getAll().length
  }

  /**
   * Check browser storage for visible state of a list of markups
   *
   * markups - Markups to lookup and set visible property on
   */
  _initVisibilityStateFromStorage(markups: Markup[]) {
    if (!markups) {
      return
    }

    if (!isStoredVisibilityLoaded()) {
      this._visibleMarkups = loadStoredVisibility()
    }

    markups.forEach((m) => {
      const storedState = this._getStoredVisibilityState(m.uuid)
      if (storedState !== undefined) {
        this._visibleMarkups[m.uuid] = storedState
      } else {
        // if no entry for this markup exists, set it as selected and save it
        this._visibleMarkups[m.uuid] = true
        this._setStoredVisibilityState(m.uuid, true)
      }
    })

    // only save to browser storage after we looped through all the markups
    saveStoredVisibility()

    // Update visible markup IDs in render service, but skip re-render
    const visibleMarkupIds = Object.keys(this._visibleMarkups).filter(
      (id) => this._visibleMarkups[id]
    )
    markupRenderService.setVisibleMarkupIds(visibleMarkupIds, true)
  }

  /**
   * Look up the visibility state for a markup from storage
   *
   * uuid - UUID of markup to lookup
   * returns - True if visible, false if hidden, undefined if nothing stored for this markup
   */
  _getStoredVisibilityState(uuid: string) {
    return getStoredVisibility(uuid)
  }

  /**
   * Save the visible state for a markup in storage
   *
   * uuid - UUID of markup to lookup
   * visible - Whether markup is visible or not
   */
  _setStoredVisibilityState(uuid: string, visible: boolean) {
    setStoredVisibility(uuid, visible)
  }

  /**
   * Get the currently used waypoint colors
   *
   * returns - List of currently used waypoint colors
   */
  getCurrentlyUsedWaypointColors() {
    const allUsedColors = this.getWaypoints()?.map((w) => {
      if (w.color) {
        return Color.fromRgbaObject(w.color as RGBA).toString()
      }
      // handle a waypoint that doesn't have a defined color, use default color
      return (markup.getDefaultColor('fish') as { dbValue: string })?.dbValue
    })
    return [...new Set(allUsedColors)].sort()
  }

  /**
   * Get the currently used waypoint types
   *
   * returns - List of currently used waypoint types
   */
  getCurrentlyUsedWaypointTypes() {
    const allUsedTypes = this.getWaypoints()?.map((w) => {
      return w.properties?.icon
    })
    return [...new Set(allUsedTypes)].sort()
  }

  /**
   * Set markup visibility for a single markup
   *
   * uuid - Markup uuid
   * setToVisible - Set markup to visible or no
   * storeValue - Whether to store the visible state or not
   */
  setVisibility(uuid: string, setToVisible: boolean, storeValue = true) {
    this._visibleMarkups[uuid] = setToVisible
    markupRenderService.setVisibility(uuid, setToVisible)

    if (storeValue) {
      this._setStoredVisibilityState(uuid, setToVisible)
    }
  }

  /**
   * Bulk set visibility for multiple markups at once.
   *
   * markupIds - Markup IDs whose visibility state should be updated.
   * setToVisible - Desired visibility state
   * storeValue - Whether to store the visible state or not
   */
  bulkSetVisibility(
    markupIds: string[],
    setToVisible: boolean,
    storeValue: boolean
  ) {
    markupRenderService.bulkSetVisibility(markupIds, setToVisible)

    for (const uuid of markupIds) {
      this._visibleMarkups[uuid] = setToVisible

      if (storeValue) {
        setStoredVisibility(uuid, setToVisible)
      }
    }
  }

  /**
   * Look up the visible state for a markup
   *
   * uuid - UUID of markup to lookup
   * return - True if visible, false if hidden, undefined if nothing stored for this markup
   */
  isVisible(uuid: string) {
    return this._visibleMarkups[uuid]
  }

  /**
   * Clear visible state for a markup (for example, on delete)
   *
   * uuid - UUID of markup to clear
   */
  _clearMarkupVisibility(uuid: string) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete this._visibleMarkups[uuid]
    clearStoredVisibility(uuid)
  }

  /**
   * Set the visibility of a list of filtered markups. Does not store visible state.
   *
   * toShow - Markup uuids to show
   */
  setFilteredVisibility(toShow: string[]) {
    const setVisible: string[] = []
    const setHidden: string[] = []
    this.getAll().forEach((m) => {
      // logic paths:
      //
      // if currently stored as visible
      //    - in filter set - show - do not store
      //    - out of filter set - hide - do not store
      //
      // if currently stored as hidden
      //    - in filter set - leave hidden
      //    - out of filter set - leave hidden
      if (this._getStoredVisibilityState(m.uuid)) {
        if (toShow.includes(m.uuid)) {
          setVisible.push(m.uuid)
        } else {
          setHidden.push(m.uuid)
        }
      }
    })
    this.bulkSetVisibility(setVisible, true, false)
    this.bulkSetVisibility(setHidden, false, false)
  }

  /**
   * Set all markups to the stored visibility state. Generally used for clearing
   * filters.
   */
  resetVisibilityToStoredState() {
    const setVisible: string[] = []
    const setHidden: string[] = []
    this.getAll().forEach((m) => {
      if (this._getStoredVisibilityState(m.uuid)) {
        setVisible.push(m.uuid)
      } else {
        setHidden.push(m.uuid)
      }
    })
    this.bulkSetVisibility(setVisible, true, false)
    this.bulkSetVisibility(setHidden, false, false)
  }

  /**
   * Updates the visibility state for all loaded markups based on the user's filters.
   */
  async updateVisibility() {
    performance.mark('start_markup_visibility')
    const markups = this.getAll()

    // load filters from storage
    await checkForExistingFilters()

    // disable any filters that are no longer valid
    await checkFilterValidity(markups)

    // get current filters
    const currentFilters = {
      searchTerm: getSearchTermFilter(),
      hasPhotos: getHasPhotosFilter(),
      contentOwners: getContentOwnersFilter(),
      sharedWith: getSharedWithFilter(),
      waypointTypes: getWaypointTypeFilter(),
      waypointColors: getWaypointColorFilter(),
    }

    // apply current filters to the list of loaded markups
    const filteredMarkups = applyFilters(markups, currentFilters)

    // set visibility based on the filtered list
    this.setFilteredVisibility(filteredMarkups)
    performance.mark('end_markup_visibility')
    performance.measure(
      'time_to_update_markup_visibility',
      'start_markup_visibility',
      'end_markup_visibility'
    )
  }

  /**
   * Find a markup by id
   *
   * @param id - ID of markup to find
   * @param includeGeo - Whether to include the geometry of the markup
   * returns - markup
   */
  getMarkupByID(id: string, includeGeo: boolean = true) {
    const markup =
      this._tracksProvider.getById(id, includeGeo) ||
      this._linesProvider.getById(id, includeGeo) ||
      this._waypointsProvider.getById(id) ||
      this._shapesProvider.getById(id, includeGeo) ||
      this._routesProvider?.getById(id, includeGeo)

    return markup
  }

  /**
   * Send create waypoint request to API and update the cache
   *
   * waypoint - the waypoint to create
   * eventProps - Additional event properties for waypoint created events
   * returns - waypoint created on the server
   */
  async createWaypoint(
    waypoint: CreateUpdateMarkupInput,
    eventProps: EventProps = {}
  ) {
    const createdWaypoint = (await this.createItem(waypoint)) as
      | Waypoint
      | undefined

    if (createdWaypoint) {
      publishWaypointCreated(createdWaypoint, eventProps)
      // Generate images for the newly created waypoint
      await this.generateWaypointImages(createdWaypoint)
    }

    return createdWaypoint
  }
  /**
   * Send create line request to API and update the cache
   *
   * line - the shape to create
   * returns - line created on the server
   */
  async createLine(
    line: CreateUpdateMarkupInput | PackageCreateUpdateMarkupInput,
    eventProps: EventProps = {}
  ) {
    // Make sure the user has drawn a line with at least 2 points before saving
    const coordinates = line.geo_json?.geometry?.coordinates
    if (!coordinates || coordinates.length < 2) {
      throw new Error('Line must have at least 2 points before saving.')
    }

    const createdLine = await this.createItem(line)

    if (createdLine) {
      publishLineCreated(createdLine as Line, eventProps)
    }

    return createdLine
  }

  /**
   * Send create shape request to API and update the cache
   *
   * shape - the shape to create
   * returns - shape created on the server
   */
  async createShape(
    shape: CreateUpdateMarkupInput | PackageCreateUpdateMarkupInput,
    eventProps: EventProps = {}
  ) {
    // Make sure the user has drawn a shape with at least 3 points before saving. Note that GeoJSON polygons have the
    // first and last coordinate repeated, so there must be 4 coordinates in the list.
    const coordinates = shape.geo_json?.geometry?.coordinates as Position[][]
    if (!coordinates?.[0] || coordinates[0].length < 4) {
      throw new Error('Area must have at least 3 points before saving')
    }

    const createdShape = await this.createItem(shape)
    if (createdShape) {
      publishShapeCreated(createdShape as Shape, eventProps)
    }

    return createdShape
  }

  /**
   * Send create track request to API and update the cache
   * Currently only used for copying shared tracks
   *
   * track - the track to create
   * returns - track created on the server
   */
  async createTrack(
    track: CreateUpdateMarkupInput | PackageCreateUpdateMarkupInput,
    eventProps: EventProps = {}
  ) {
    // Make sure the user has drawn a track with at least 2 points before saving
    const coordinates = track.geo_json?.geometry?.coordinates
    if (!coordinates || coordinates.length < 2) {
      throw new Error('Track must have at least 2 points before saving.')
    }

    const createdTrack = await this.createItem(track)

    if (createdTrack) {
      publishTrackCreated(createdTrack as Track, eventProps)
    }

    return createdTrack
  }

  /**
   * Create markup using the supplied content provider
   *
   * contentProvider - Content Provider to use for creating
   * contentItem - Item to create
   * returns - The markup item that was created
   */
  async createItem(
    contentItem:
      | CreateUpdateMarkupInput
      | RawRoute
      | PackageCreateUpdateMarkupInput
  ): Promise<Markup | undefined> {
    performance.mark('time_to_create_markup_started')

    let response

    // Using 'in' type guard because RawRoute does not declare a 'type' property.
    const contentItemType =
      'type' in contentItem && contentItem.type
        ? contentItem.type
        : MarkupType.route

    // Dispatch to the package provider for the markup type.
    if (contentItemType === MarkupType.line) {
      response = await this._linesProvider.create(
        contentItem as CreateUpdateLineInput
      )
    } else if (contentItemType === MarkupType.track) {
      response = await this._tracksProvider.create(
        contentItem as CreateUpdateTrackInput
      )
    } else if (contentItemType === MarkupType.shape) {
      response = await this._shapesProvider.create(
        contentItem as CreateUpdateShapeInput
      )
    } else if (contentItemType === MarkupType.route) {
      response = await this._routesProvider?.create(
        contentItem as unknown as CreateRouteInput
      )
    } else if (contentItemType === MarkupType.waypoint) {
      response = await this._waypointsProvider.create(
        contentItem as CreateUpdateWaypointInput
      )
    }

    if (response) {
      this.setVisibility(response.uuid, true)
      setMarkupLocations([response])
      await markupRenderService.addMarkup(response)

      performance.mark('time_to_create_markup_finished')

      const markupCreation = performance.measure(
        'time_to_create_markup',
        'time_to_create_markup_started',
        'time_to_create_markup_finished'
      )

      const account_id = await getUserId()
      log('account_markup_creation', {
        account_id,
        markup_uuid: response.uuid,
        markup_type: response.type,
        markup_creation_time: markupCreation?.duration,
      })
    }

    return response
  }

  /**
   * Update waypoint request API and update the cache
   *
   * waypoint - the waypoint to update
   * eventProps - Additional event properties for waypoint updated events
   * returns - waypoint that was updated
   */
  async updateWaypoint(
    waypoint: CreateUpdateMarkupInput,
    eventProps: EventProps = {}
  ) {
    const updatedWaypoint = await this.updateItem(waypoint)
    if (updatedWaypoint) {
      publishWaypointUpdated(updatedWaypoint as Waypoint, eventProps)
    }

    return updatedWaypoint
  }

  /**
   * Generate waypoint images for a waypoint
   * @async
   * @param waypoint - The waypoint to generate images for
   * @param waypoint.icon - The waypoint's icon code
   * @param waypoint.color - The waypoint's color as an rgba array
   * @param [params] - Parameter object
   * @param [params.icon] - Override icon code
   * @param [params.color] - Override color as an rgba array (will be converted to Color object)
   * @param [params.selected=false] - Whether the icon is selected (unused but kept for API compatibility)
   * @returns Promise that resolves to array of generated images
   */
  // eslint-disable-next-line require-await
  async generateWaypointImages(
    waypoint: Waypoint,
    params: {
      icon?: string
      color?: number[] | RGBA
      selected?: boolean
    } = {}
  ): Promise<WaypointImage[]> {
    // Package waypoints store icon in properties.icon
    const waypointIcon = (waypoint as Waypoint).properties?.icon || 'Location'
    const waypointColor = waypoint.color
    const { icon = waypointIcon, color = waypointColor } = params

    // Package waypoints have color as RGBA object; normalize to array for image generation
    const colorArray = Color.fromRgbaObject(color as RGBA).toRgbaArray()

    const colorObj = Color.fromRgbaArray(colorArray)

    /* Generate both the selected and unselected images so that when the image
       is clicked it doesn't flicker or hide while it generates first, but still
       return the correct image. */
    const imgs = Promise.all([
      waypointImageService.getImageOrGenerate(icon, colorObj),
      waypointImageService.getImageOrGenerate(icon, colorObj, 'highlighted'),
      waypointImageService.getImageOrGenerate(icon, colorObj, 'lowZoom'),
    ])

    return imgs
  }

  /**
   * Update shape request API and update the cache
   *
   * shape - the shape to update
   * returns - shape that was updated
   */
  async updateShape(
    shape: CreateUpdateMarkupInput | PackageCreateUpdateMarkupInput,
    eventProps: EventProps = {}
  ) {
    // Make sure the user has drawn a shape with at least 3 points before saving. Note that GeoJSON polygons have the
    // first and last coordinate repeated, so there must be 4 coordinates in the list.
    const coordinates = shape.geo_json?.geometry?.coordinates as Position[][]
    if (!coordinates?.[0] || coordinates[0].length < 4) {
      throw new Error('Area must have at least 3 points before saving')
    }

    const updatedShape = await this.updateItem(shape)

    if (updatedShape) {
      publishShapeUpdated(updatedShape as Shape, eventProps)
    }

    return updatedShape
  }

  /**
   * Update line request API and update the cache
   *
   * line - the line to update
   * returns - line that was updated
   */
  async updateLine(
    line: CreateUpdateMarkupInput | PackageCreateUpdateMarkupInput,
    eventProps: EventProps = {}
  ) {
    // Make sure the user has drawn a line with at least 2 points before saving
    const coordinates = line.geo_json?.geometry?.coordinates
    if (!coordinates || coordinates.length < 2) {
      throw new Error('Line must have at least 2 points before saving.')
    }

    const updatedLine = await this.updateItem(line)

    if (updatedLine) {
      publishLineUpdated(updatedLine as Line, eventProps)
    }

    return updatedLine
  }

  /**
   * Update track request API and update the cache
   *
   * track - the track to update
   * returns - track that was updated
   */
  async updateTrack(
    track: CreateUpdateMarkupInput | PackageCreateUpdateMarkupInput,
    eventProps: EventProps = {}
  ) {
    // Make sure the user has drawn a track with at least 2 points before saving
    const coordinates = track.geo_json?.geometry?.coordinates
    if (!coordinates || coordinates.length < 2) {
      throw new Error('Track must have at least 2 points before saving.')
    }

    const updatedTrack = await this.updateItem(track)

    if (updatedTrack) {
      publishTrackUpdated(updatedTrack as Track, eventProps)
    }

    return updatedTrack
  }

  /**
   * Update markup using the supplied content provider
   *
   * contentProvider - Content Provider to use for updating
   * contentItem - Item to update
   * returns - The markup item that was updated
   */
  async updateItem(
    contentItem:
      | CreateUpdateMarkupInput
      | PackageCreateUpdateMarkupInput
      | PackageUpdateRouteInput
      | { uuid: string; data: UpdateRouteInput }
  ): Promise<Markup | undefined> {
    performance.mark('time_to_edit_markup_started')

    let response

    // Using 'in' type guard because RawRoute does not declare a 'type' property.
    const contentItemType =
      'type' in contentItem && contentItem.type
        ? contentItem.type
        : MarkupType.route

    // Dispatch to the package provider for the markup type.
    if (contentItemType === MarkupType.line) {
      response = await this._linesProvider.update(
        contentItem as CreateUpdateLineInput
      )
    } else if (contentItemType === MarkupType.track) {
      response = await this._tracksProvider.update(
        contentItem as CreateUpdateTrackInput
      )
    } else if (contentItemType === MarkupType.shape) {
      response = await this._shapesProvider.update(
        contentItem as CreateUpdateShapeInput
      )
    } else if (contentItemType === MarkupType.route) {
      response = await this._routesProvider?.update(
        contentItem as unknown as PackageUpdateRouteInput
      )
    } else if (contentItemType === MarkupType.waypoint) {
      response = await this._waypointsProvider.update(
        contentItem as CreateUpdateWaypointInput
      )
    }

    if (response) {
      this.setVisibility(response.uuid, true)
      setMarkupLocations([response])
      await markupRenderService.updateMarkup(response)

      performance.mark('time_to_edit_markup_finished')

      const markupEdit = performance.measure(
        'time_to_edit_markup',
        'time_to_edit_markup_started',
        'time_to_edit_markup_finished'
      )

      const account_id = await getUserId()
      log('account_markup_edit', {
        account_id,
        markup_uuid: response.uuid,
        markup_type: response.type,
        markup_edit_time: markupEdit?.duration,
      })
    }

    return response
  }

  /**
   * Update pending photos for a waypoint after upload completion or cancellation
   *
   * waypoint - The waypoint to update photos for
   */
  updateWaypointPendingPhotos(waypoint: Waypoint) {
    // Handle package waypoints - update photo URLs from recently uploaded photos
    const packageWaypoint = waypoint as Waypoint

    for (const photo of packageWaypoint.photos ?? []) {
      const recentlyUploadedPhoto = getPhotoById(photo.uuid)

      if (photo.url === '' && recentlyUploadedPhoto?.url) {
        photo.url = recentlyUploadedPhoto.url
      }
    }
    // Filter displayable photos and sort by upload date
    const filteredPhotos = packageWaypoint.photos?.filter(
      (photo) => !!photo.url
    )
    if (filteredPhotos) {
      packageWaypoint.photos = sortBy(filteredPhotos, ['createdAtClient'])
    }
  }

  /**
   * Delete a photo from a waypoint
   *
   * @param waypoint - The waypoint to delete the photo from
   * @param photoUUID - UUID of the photo to delete
   */
  async deleteWaypointPhoto(waypoint: Waypoint, photoUUID: string) {
    const requestBody = (waypoint as Waypoint).generateRequestObject({
      attachments: {
        photos: {
          add: [],
          remove: [{ uuid: photoUUID }],
        },
      },
    })

    await this._waypointsProvider.update(
      requestBody as CreateUpdateWaypointInput
    )
  }

  /**
   * Send delete waypoint request to API and update the cache
   *
   * uuid - ID of markup to delete
   * eventProps - Additional event properties for waypoint deleted events
   */
  async deleteWaypoint(uuid: string, eventProps: EventProps = {}) {
    const waypointToDelete = this.getMarkupByID(uuid) as Waypoint

    await this.deleteItem(this._waypointsProvider, uuid, MarkupType.waypoint)

    if (waypointToDelete) {
      publishWaypointDeleted(waypointToDelete, eventProps)
    }
  }

  /**
   * Send delete shape request to API and update the cache
   *
   * uuid - ID of markup to delete
   */
  async deleteShape(uuid: string, eventProps: EventProps = {}) {
    const shapeToDelete = this.getMarkupByID(uuid) as Shape

    await this.deleteItem(this._shapesProvider, uuid, MarkupType.shape)

    if (shapeToDelete) {
      publishShapeDeleted(shapeToDelete, eventProps)
    }
  }

  /**
   * Send delete line request to API and update the cache
   *
   * uuid - ID of markup to delete
   */
  async deleteLine(uuid: string, eventProps: EventProps = {}) {
    const lineToDelete = this.getMarkupByID(uuid) as Line

    await this.deleteItem(this._linesProvider, uuid, MarkupType.line)

    if (lineToDelete) {
      publishLineDeleted(lineToDelete, eventProps)
    }
  }

  /**
   * Send delete track request to API and update the cache
   *
   * uuid - ID of markup to delete
   */
  async deleteTrack(uuid: string, eventProps: EventProps = {}) {
    const trackToDelete = this.getMarkupByID(uuid) as Track

    await this.deleteItem(this._tracksProvider, uuid, MarkupType.track)

    if (trackToDelete) {
      publishTrackDeleted(trackToDelete, eventProps)
    }
  }

  /**
   * Delete markup using the supplied content provider
   *
   * contentProvider - Content Provider to use for deleting
   * uuid - Item ID to delete
   * type - Item type to delete
   */
  async deleteItem<
    T extends Markup,
    U extends CreateUpdateMarkupInput | RawRoute,
    V extends
      | CreateUpdateMarkupInput
      | { uuid: string; data: UpdateRouteInput },
  >(
    contentProvider: ContentProvider<T, U, V> | PackageContentProvider,
    uuid: string,
    type: MarkupType
  ) {
    performance.mark('time_to_delete_markup_started')

    await contentProvider.delete?.(uuid)

    this._removeMarkupFromCollections(uuid)
    this._clearMarkupVisibility(uuid)

    await markupRenderService.removeMarkup({ uuid })

    performance.mark('time_to_delete_markup_finished')

    const markupDeletion = performance.measure(
      'time_to_delete_markup',
      'time_to_delete_markup_started',
      'time_to_delete_markup_finished'
    )

    const account_id = await getUserId()

    log('account_markup_deletion', {
      account_id,
      markup_uuid: uuid,
      markup_type: type,
      markup_deletion_time: markupDeletion?.duration,
    })
  }

  /**
   * Delete a set of markups at once. Markups and routes are deleted through
   * separate apis; whatever was deleted is removed from local state even if the
   * other request fails, and the first failure is rethrown afterwards.
   *
   * toRemove - array of markups to be deleted
   * eventProps - Additional event properties for markups bulk delete events
   */
  async deleteMarkups(
    toRemove: Array<Markup>,
    eventProps: { origin?: string } = {}
  ) {
    // Separate content by type, to direct to different apis.
    const markupIds = []
    const routeIds = []

    for (const item of toRemove) {
      const { type, uuid } = item
      if (type === 'route') {
        routeIds.push(uuid)
      } else {
        markupIds.push(uuid)
      }
    }

    const markupResult = await deleteInChunks(markupIds, (uuids) =>
      httpPostJson('/v1/markups/bulk_delete', { uuids })
    )
    const routeResult = await deleteInChunks(routeIds, deleteRoutes)

    if (routeResult.deletedIds.length) {
      const currentUserId = await getUserId()

      for (const routeId of routeResult.deletedIds) {
        const markup = this.getMarkupByID(routeId) as Route

        publishRouteDeleted(markup, {
          origin: 'bulk delete',
          ...eventProps,
        })

        const isShared = markup?.isShared

        if (isShared && currentUserId) {
          removeShareGrantForEntity(routeId, 'route', currentUserId)
        }
      }
    }

    this.publishBulkMarkupDeletedEvents(markupResult.deletedIds, eventProps)
    this.removeMarkups([...markupResult.deletedIds, ...routeResult.deletedIds])

    // checkStatus rejects with the parsed response body, which can be falsy
    const failed = [markupResult, routeResult].find(
      (result) => 'error' in result
    )
    if (failed) {
      throw failed.error
    }
  }

  /**
   * Send individual marketing events for each markup being deleted
   *
   * markupIds - Ids of markups to publish events for
   * eventProps - Additional marketing event props
   */
  publishBulkMarkupDeletedEvents(
    markupIds: string[],
    eventProps: { origin?: string } = {}
  ) {
    for (const id of markupIds) {
      const markupVm = this.getMarkupByID(id)
      if (!markupVm) {
        continue
      }

      if (isShape(markupVm)) {
        publishShapeDeleted(markupVm, eventProps)
      } else if (isWaypoint(markupVm)) {
        publishWaypointDeleted(markupVm, eventProps)
      } else if (isLine(markupVm)) {
        publishLineDeleted(markupVm, eventProps)
      } else if (isTrack(markupVm)) {
        publishTrackDeleted(markupVm, eventProps)
      }
    }
  }

  /**
   * Remove the deleted markup from the store array. Also used to remove markups
   * associated with a deleted shared folder.
   *
   * toRemove - array of markup ids that were deleted
   */
  removeMarkups(toRemove: string[]) {
    this._linesProvider.bulkRemove(toRemove)
    this._waypointsProvider.bulkRemove(toRemove)
    this._shapesProvider.bulkRemove(toRemove)
    this._tracksProvider.bulkRemove(toRemove)
    this._routesProvider?.bulkRemove(toRemove)

    toRemove.forEach((uuid: string) => {
      this._clearMarkupVisibility(uuid)
      this._removeMarkupFromCollections(uuid)
    })

    markupRenderService.removeMarkups(toRemove)
  }

  /**
   * Gets the focused markup data.
   */
  getFocusedMarkup() {
    return this._focusedMarkup
  }

  /**
   * Is a markup focused and in any FOCUSED_MARKUP_MODES
   */
  isMarkupBeingAuthored() {
    return this.getFocusedMarkup().mode !== ''
  }

  /**
   * Focuses a markup for detail or edit.
   *
   * uuid - Markup UUID to focus.
   * throws if no UUID is given or markup is not found.
   */
  focusMarkup(uuid?: string) {
    if (!uuid) {
      throw Error('UUID is required')
    }

    const previousUuid = this._focusedMarkup.markup?.uuid

    if (uuid === previousUuid) {
      return this._focusedMarkup
    }

    const markup = this.getMarkupByID(uuid)

    if (!markup) {
      throw Error(
        `Markup ${uuid} not found! If creating a new markup, use focusNewMarkup instead`
      )
    }

    this.clearFocusedMarkup(true)
    this.setVisibility(uuid, true, true)
    this._focusedMarkup.markup = cloneDeep(markup)

    markupRenderService.clearHighlightedMarkups(true)
    markupRenderService.highlightMarkup(markup)

    return this._focusedMarkup
  }

  /**
   * Focuses a new markup for detail or edit.
   *
   * markup - Markup GeoJSON data.
   * markupVm - Markup view-model instance.
   * throws if markup already exists.
   */
  focusNewMarkup(markup: Markup) {
    if (this.getMarkupByID(markup.uuid)) {
      throw Error('Markup already exists, use focusMarkup instead')
    }

    this.clearFocusedMarkup(true)
    this.setVisibility(markup.uuid, true)
    this._focusedMarkup.markup = cloneDeep(markup)

    markupRenderService.clearHighlightedMarkups(true)
    markupRenderService.highlightMarkup(markup)

    return this._focusedMarkup
  }

  /**
   * Clears the currently focused markup.
   */
  clearFocusedMarkup(skipRedraw?: boolean) {
    this._focusedMarkup.markup = null
    this._focusedMarkup.mode = ''
    this._focusedMarkup.hasUnsavedChanges = false
    markupRenderService.clearFocusedMarkup(skipRedraw)
  }

  /**
   * Sets the focused markup to edit mode.
   *
   * changes - Markup changes to merge with the current markup.
   */
  editFocusedMarkup(
    changes: {
      attachments?: { photos: MarkupPhoto[] }
      color?: number[] | string
      geo_json?: Partial<Feature>
      name?: string
      notes?: string | null
    } = {}
  ) {
    const focusedMarkup = this._focusedMarkup.markup

    if (!focusedMarkup) {
      throw new Error(
        'Must focus a new or existing markup before attempting to edit'
      )
    }

    const originalMarkup = this.getAllVisible().find(
      (m) => m.uuid === focusedMarkup.uuid
    )

    if ('photos' in focusedMarkup) {
      focusedMarkup.photos = (changes.attachments?.photos ??
        focusedMarkup.photos) as (typeof focusedMarkup)['photos']
    }
    focusedMarkup.color =
      (changes.color as unknown as RGBA) ?? focusedMarkup.color
    focusedMarkup.name = changes.name ?? focusedMarkup.name
    focusedMarkup.notes =
      changes.notes === undefined ? focusedMarkup.notes : changes.notes

    // Shapes, lines, and tracks carry in-progress style edits (color, style,
    // weight, fill) that must be committed onto the markup before saving.
    if (
      isShape(focusedMarkup) ||
      isLine(focusedMarkup) ||
      isTrack(focusedMarkup)
    ) {
      const packageFocusedMarkup = focusedMarkup as Shape | Line | Track
      packageFocusedMarkup.color =
        packageFocusedMarkup.inProgressColor ?? packageFocusedMarkup.color
      packageFocusedMarkup.style =
        packageFocusedMarkup.inProgressStyle ?? packageFocusedMarkup.style
      packageFocusedMarkup.weight =
        packageFocusedMarkup.inProgressWeight ?? packageFocusedMarkup.weight
      if (isShape(packageFocusedMarkup)) {
        packageFocusedMarkup.fillColor =
          packageFocusedMarkup.inProgressFillColor ??
          packageFocusedMarkup.fillColor
      }
    }

    if (changes.geo_json) {
      // Type guard to separate package markup view models from internal markup view models
      if (
        isShape(focusedMarkup) ||
        isLine(focusedMarkup) ||
        isTrack(focusedMarkup)
      ) {
        // extract geo json types since they are not exported from markups package
        if (isShape(focusedMarkup)) {
          type ShapeGeoJsonFeature = Parameters<Shape['updateGeoJSON']>[0]
          focusedMarkup.updateGeoJSON(changes.geo_json as ShapeGeoJsonFeature)
        } else if (isLine(focusedMarkup)) {
          type LineGeoJsonFeature = Parameters<Line['updateGeoJSON']>[0]
          focusedMarkup.updateGeoJSON(changes.geo_json as LineGeoJsonFeature)
        } else if (isTrack(focusedMarkup)) {
          type TrackGeoJsonFeature = Parameters<Track['updateGeoJSON']>[0]
          focusedMarkup.updateGeoJSON(changes.geo_json as TrackGeoJsonFeature)
        }
      } else {
        // Internal markup view models
        const markup = focusedMarkup as Waypoint

        markup.updateGeoJSON(
          changes.geo_json as Parameters<Waypoint['updateGeoJSON']>[0]
        )
      }
    }

    if (!isEqual(originalMarkup, focusedMarkup)) {
      this._focusedMarkup.hasUnsavedChanges = true
    }
  }

  /**
   * Sets the draw mode for the focused markup.
   */
  setFocusedMarkupMode(mode: string = 'editing') {
    if (!FOCUSED_MARKUP_MODES.includes(mode)) {
      const modes = FOCUSED_MARKUP_MODES.map((mode) => `"${mode}"`).join(', ')
      throw Error(`drawMode must be one of: [${modes}]`)
    }

    this._focusedMarkup.mode = mode

    if (this._focusedMarkup.markup) {
      markupRenderService.setFocusedMarkupId(this._focusedMarkup.markup.uuid)
    }
  }

  /**
   * Returns the Set of uuid's of markup that should be highlighted
   */
  getHighlightedMarkups() {
    return this._highlightedMarkups
  }

  /**
   * Add a markup to the highlighted array
   */
  addMarkupHighlight(uuid: string) {
    if (!this._highlightedMarkups.has(uuid)) {
      this._highlightedMarkups.add(uuid)
      const markup = this.getMarkupByID(uuid)
      if (markup) {
        markupRenderService.highlightMarkup(markup)
      }
    }
  }

  /**
   * Remove a markup from the highlighted array. Do not use for multiple
   * selections at once. findIndex and then splice takes a long time if run
   * multiple times in a row.
   */
  removeMarkupHighlight(uuid: string) {
    if (this._highlightedMarkups.has(uuid)) {
      this._highlightedMarkups.delete(uuid)
      const markup = this.getMarkupByID(uuid)
      if (markup) {
        markupRenderService.removeMarkupHighlight(markup)
      }
    }
  }

  /**
   * Toggle highlight for a group of markups
   *
   * toToggle - markups uuids' to toggle
   * highlight - whether to highlight or remove the highlight
   */
  toggleMultipleHighlights(toToggle: Set<string>, highlight: boolean) {
    if (highlight) {
      toToggle.forEach((id) => this._highlightedMarkups.add(id))
    } else {
      toToggle.forEach((id) => this._highlightedMarkups.delete(id))
    }
    markupRenderService.setHighlightedMarkupIds(this._highlightedMarkups)
  }

  /**
   * Reset the highlighted array to empty
   */
  clearHighlightedMarkups() {
    this._highlightedMarkups = new Set()
    markupRenderService.clearHighlightedMarkups()
  }

  /**
   * Is the markup highlighted on the map
   *
   * uuid - uuid of the markup
   */
  isMarkupHighlighted(uuid: string) {
    return this._highlightedMarkups.has(uuid)
  }

  /**
   * Handle new photos that have been uploaded
   *
   * @param toAdd - Photos array
   * @param location (optional). If passed in, waypoint location will be updated.
   */
  async newPhotosUploaded(
    waypoint: Waypoint,
    toAdd: MarkupPhoto[],
    location?: Geographic
  ) {
    const requestBody: {
      attachments: {
        photos: {
          add: { uuid: string }[]
          remove: { uuid: string }[]
        }
      }
    } = {
      attachments: {
        photos: {
          add: toAdd.map((photo) => {
            return { uuid: photo.uuid }
          }),
          remove: [],
        },
      },
    }

    if (location) {
      waypoint.location = location.toDegreesTuple()

      // update icon location
      const focusedMarkup = markupService.getFocusedMarkup()?.markup
      if (waypoint.location && focusedMarkup?.uuid === waypoint.uuid) {
        // waypoint.location is guaranteed truthy by the enclosing guard.
        focusedMarkup.toGeoJSON().geometry.coordinates = waypoint.location
      }

      // pan the map if not in view
      if (!isInView(waypoint.toGeoJSON())) {
        panToMarkup(waypoint)
      }
    }

    const updatedWaypoint = await this.updateWaypoint(
      waypoint.generateRequestObject(requestBody),
      {}
    )

    // Copy the photos from the API response back to the original waypoint object
    // so the component's reference gets updated
    if (updatedWaypoint) {
      waypoint.photos = (updatedWaypoint as Waypoint).photos
    }

    // add temporary urls to photos that don't have them set (cloudinary still
    // syncing with photo service)
    waypoint.photos?.forEach((p) => {
      if (p.url === '') {
        const urlFromInitialUpload = toAdd.find((t) => t.uuid === p.uuid)?.url
        p.url = urlFromInitialUpload
        p.thumbnailUrl = urlFromInitialUpload
      }
    })
  }

  /**
   * Calculates bounding box coordinates of an array of markups
   */
  getMarkupArrayBounds(markups: Array<Markup>) {
    const allCoordPoints: Position[] = []

    // flatten all point, line, shapes into a single array
    markups.forEach((markup) => {
      let markupGeometry: Point | Polygon | LineString | undefined =
        markup?.toGeoJSON().geometry
      // lookup markup geometry for any markup that doesn't have it populated
      if (!markup?.toGeoJSON().geometry) {
        const markupWithGeo = this.getMarkupByID(markup.uuid)
        markupGeometry = markupWithGeo?.toGeoJSON().geometry
      }

      let markupType: 'LineString' | 'Polygon' | 'Point' | 'Route' | undefined =
        markupGeometry?.type
      if (isRoute(markup)) {
        // route objects do not have a type property, but do have a route property
        markupType = 'Route'
      }

      const coordinates = markupGeometry?.coordinates
      if (
        markupType !== 'Route' &&
        (!coordinates || !Array.isArray(coordinates))
      ) {
        return
      }

      if (markupType === 'Point' && coordinates && coordinates.length >= 2) {
        allCoordPoints.push(coordinates as Position)
      } else if (markupType === 'LineString') {
        allCoordPoints.push(...(coordinates as Position[]))
      } else if (markupType === 'Polygon') {
        allCoordPoints.push(...(coordinates as Position[][])[0])
      } else if (markupType === 'Route') {
        // consolidate all of the legs' waypoints into a single coordinate array
        const routeLegs = (markup as Route).rawData?.route?.legs
        routeLegs?.forEach((l) => {
          const routeWaypointCoords = l.waypoints.map((w) => w.location)
          allCoordPoints.push(...routeWaypointCoords)
        })
      }
    })

    const lats = allCoordPoints.map((points) => points[0])
    const lons = allCoordPoints.map((points) => points[1])

    const maxLat = max(lats)
    const maxLon = max(lons)
    const minLat = min(lats)
    const minLon = min(lons)

    return [
      [minLat, minLon],
      [maxLat, maxLon],
    ]
  }

  /**
   * Returns the type label for a given markup type.
   *
   * type - Markup type
   * [opts] - Options:
   *    - {boolean} plural - If true, returns the plural label instead of
   *        singular. Defaults to false.
   *    - {boolean} lowercase - If true, return the label in lowercase.
   *        Otherwise, it will be returned in title case. Defaults to false,
   *        i.e., title case.
   *    - {String} fallback - Fallback text to use (defaults to 'Item').
   * returns - Type label
   */
  getMarkupTypeLabel(
    type: MarkupType,
    opts: {
      plural?: boolean
      lowercase?: boolean
      fallback?: string
    }
  ) {
    const defaultFallback = opts?.plural ? 'Items' : 'Item'
    opts ??= { fallback: defaultFallback }
    let label = ''
    if (type) {
      label = opts.plural
        ? MARKUP_TYPE_LABELS_PLURAL[type]
        : MARKUP_TYPE_LABELS[type]
    }
    label ??= opts.fallback ?? ''
    if (opts.lowercase) {
      label = label.toLowerCase()
    }
    return label
  }

  /**
   * Remove the markup from the array by uuid
   *
   * array - the array to delete the markup from
   * markup - object to remove from the array
   */
  _removeMarkupFromArray(array: Array<Markup>, markup: Markup) {
    const indexToRemove = array.findIndex((x) => x.uuid === markup.uuid)
    array.splice(indexToRemove, 1)
  }

  /**
   * Remove a markup from all collections it is currently in
   *
   * uuid - markup uuid to remove
   */
  _removeMarkupFromCollections(uuid: string) {
    const collections = contentCollectionService.getCollectionsForMarkup(uuid)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    collections.forEach((collection: any) => {
      contentCollectionService._removeEntity(uuid, collection.uuid)
    })
  }

  /**
   * Count up the markups, points in markups, and log them to firebase
   *
   * loadTimes - Timings for loading markups
   */
  async _logMarkupDistribution() {
    const account_id = await getUserId()
    const distributionInfo = {
      account_id,
      num_waypoints: this.getWaypoints().length,
      num_lines: this.getLines().length,
      num_shapes: this.getShapes().length,
      num_tracks: this.getTracks().length,
      num_routes: this.getRoutes().length,
      num_markups_total:
        this.getWaypoints().length +
        this.getLines().length +
        this.getShapes().length +
        this.getTracks().length +
        this.getRoutes().length,
      num_line_coordinates: 0,
      num_track_coordinates: 0,
      num_shape_coordinates: 0,
      num_route_coordinates: 0,
      num_coordinates_total: 0,
    }
    distributionInfo.num_line_coordinates = this.getLines(true).reduce(
      (total, line) => {
        return total + (line?.coordinates?.length ?? 0)
      },
      0
    )
    distributionInfo.num_track_coordinates = this.getTracks(true).reduce(
      (total, track) => {
        return total + (track?.coordinates?.length ?? 0)
      },
      0
    )
    distributionInfo.num_shape_coordinates = this.getShapes(true).reduce(
      (total, shape) => {
        return total + (shape?.coordinates[0]?.length ?? 0)
      },
      0
    )
    distributionInfo.num_route_coordinates = this.getRoutes(true).reduce(
      (total, route) => {
        const routeLength = (route as Route).geoJson?.geometry?.coordinates
          ?.length
        return total + (routeLength ?? 0)
      },
      0
    )
    distributionInfo.num_coordinates_total =
      this.getWaypoints().length +
      distributionInfo.num_line_coordinates +
      distributionInfo.num_track_coordinates +
      distributionInfo.num_shape_coordinates +
      distributionInfo.num_route_coordinates

    marketingEventService.publish('markups_loaded', {
      markup_count: distributionInfo.num_markups_total,
      waypoint_count: distributionInfo.num_waypoints,
      line_count: distributionInfo.num_lines,
      area_count: distributionInfo.num_shapes,
      track_count: distributionInfo.num_tracks,
      route_count: distributionInfo.num_routes,
      num_points: distributionInfo.num_coordinates_total,
    })
  }

  /**
   * Routes integration
   * ------------------
   */

  /**
   * Returns the routes collection.
   * @param includeGeo - Whether to include the geojson or not
   *
   * returns - Routes as markup.
   */
  getRoutes(includeGeo = false): Route[] {
    if (!this._routesProvider) {
      return []
    }
    return !includeGeo
      ? this._routesProvider.getWithoutGeo()
      : this._routesProvider.get()
  }

  /**
   * Send create route request to API and update the cache
   *
   * route - route to create
   * eventProps - properties to add to the saved event
   */
  async createRoute(route: RawRoute, eventProps: EventProps = {}) {
    // Ensure the async-created routes provider has been built before the guard below.
    await this._ensureUserId()

    const createdRoute = await this.createItem(route)

    if (createdRoute) {
      this.focusMarkup(createdRoute.uuid)
      publishRouteSaved(createdRoute as Route, eventProps)
    }

    return createdRoute
  }

  /**
   * Update route request API and update the cache
   *
   * route - route that was updated
   */
  async updateRoute(
    route: {
      uuid: string
      data: UpdateRouteInput
    },
    eventProps: EventProps = {}
  ) {
    // Transform legacy nested format to package flat format
    const routeInput = {
      uuid: route.uuid,
      ...route.data,
    } as unknown as PackageUpdateRouteInput

    const updatedRoute = await this.updateItem(routeInput)

    if (updatedRoute) {
      publishRouteUpdated(updatedRoute as Route, eventProps)
    }

    return updatedRoute
  }

  /**
   * Deletes a route in the markup collection.
   */
  async deleteRoute(routeId: string, eventProps: EventProps) {
    // Ensure the async-created routes provider has been built before the guard below.
    await this._ensureUserId()

    const originalRoute = this.getMarkupByID(routeId) as Route

    if (this._routesProvider) {
      await this.deleteItem(this._routesProvider, routeId, MarkupType.route)
    }

    publishRouteDeleted(originalRoute, eventProps)

    // manually remove shared route
    const currentUserId = await getUserId()

    const isShared = originalRoute?.isShared

    if (isShared && currentUserId) {
      await removeShareGrantForEntity(routeId, 'route', currentUserId)
    }
  }

  /**
   * Given a shared route, create a copy
   *
   * Extracted from copySharedMarkup since routes have a
   * slightly different implementation from the rest of our markups
   *
   * markup - route to be copied
   * returns - the new route
   */
  // eslint-disable-next-line require-await
  async copySharedRoute(markup: Route) {
    const rawData = markup.rawData
    if (!rawData) {
      return
    }

    // Package routes: server generates ID
    const copy = {
      color: rawData.color,
      name: rawData.name,
      notes: rawData.notes ?? '',
      deleted: false,
      route: rawData.route,
      style: rawData.style,
      weight: rawData.weight,
      createdAt: new Date().toISOString(),
      lastModifiedAt: new Date().toISOString(),
    }
    return this.createRoute(copy as unknown as RawRoute)
  }

  /**
   * Given a shared markup id, create a copy of that markup
   *
   * returns - the new route
   */
  // eslint-disable-next-line require-await
  async copySharedMarkup(id: string) {
    const markup = this.getMarkupByID(id)

    if (!markup) {
      return
    }

    if (isRoute(markup)) {
      return this.copySharedRoute(markup)
    }

    const copy = {
      name: markup.name,
      type: markup.type as MarkupType,
      color: markup.color,
      geo_json: markup.toGeoJSON(),
      notes: markup.notes,
      uuid: uuid(),
      application: 'fish',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    switch (markup.type) {
      case 'waypoint':
        return this.createWaypoint(copy as PackageCreateUpdateMarkupInput)
      case 'line':
        return this.createLine(copy as CreateUpdateMarkupInput)
      case 'shape':
        return this.createShape(copy as PackageCreateUpdateMarkupInput)
      case 'track':
        return this.createTrack(copy as CreateUpdateMarkupInput)
    }
  }

  /**
   * Return the geojson for Mapbox to render for the markup currently being edited,
   * or null if no markup is being edited
   */
  getEditingMarkupGeoJson(): GeoJSON | null {
    return FOCUSED_MARKUP_EDIT_MODES.includes(this._focusedMarkup.mode) &&
      this._focusedMarkup.markup
      ? generateMarkupGeoJson(this._focusedMarkup.markup, true)
      : null
  }

  /**
   * Return the geojson for Mapbox to render for the markup currently being opened
   * in a View Mode
   */
  getViewModeMarkupGeoJson(): GeoJSON | null {
    return FOCUSED_MARKUP_VIEW_MODES.includes(this._focusedMarkup.mode) &&
      this._focusedMarkup.markup
      ? generateMarkupGeoJson(this._focusedMarkup.markup, true)
      : null
  }

  /**
   * Returns true if the specified markup name is a custom name (rather than the auto-generated name)
   *
   * name - Markup name
   */
  isCustomMarkupName(name: string) {
    return !DEFAULT_MARKUP_NAME_REGEX.test(name)
  }

  getMarkupTypesOrderForCurrentApp() {
    return MARKUP_TYPE_ORDER_BY_APP['fish'] || []
  }

  /**
   * Sends marketing event when user opens all content markup list or any
   * dedicated markup type list i.e. "all_content_list_viewed" or "waypoint_list_viewed"
   *
   * route - current route
   * markupType - Type of markup used to determine event name
   * markupCount - Total number of markups
   * sortOrder - Current selected sort order
   * visibility - Whether any markups are hidden or not
   * currentFilters - Object containing all currently applied filters
   */
  sendListViewEvent(
    route: RouteLocationNormalizedLoaded,
    markupType: MarkupType,
    markupCount: number,
    sortOrder: 'type' | 'newToOld' | 'oldToNew' | 'ascending' | 'descending',
    visibility: boolean,
    currentFilters: {
      searchTerm: string
      hasPhotos: boolean
      contentOwners: string[]
      sharedWith: string[]
      waypointTypes: string[]
      waypointColors: string[]
      onlyOnMap: boolean
    }
  ) {
    // Do not send event again when user closes select card or filter card
    // within same list context
    if (
      route.meta?.fromRoute !== `filter-content-fish` &&
      typeof route.meta?.fromRoute === 'string' &&
      !route.meta?.fromRoute?.startsWith('content-select')
    ) {
      marketingEventService.publish(`${markupType}_list_viewed`, {
        origin: getContentTypeListOrigin(route.meta?.fromRoute),
        markup_count: markupCount,
        sort_type: getSortTypeEventValue(sortOrder),
        visibility: visibility,
        filters: getFilterTypeListValue(currentFilters),
      })
    }
  }

  /**
   * Find the markups that lie within a specified geometry
   *
   * @param geometry - Geometry to look for markup within
   * @param includeShared - Whether to include shared content
   * @return Markups that lie within the geometry
   */
  getMarkupsInGeometry(geometry: MultiPolygon, includeShared: boolean = true) {
    const markups = this.getAll(true)
    const geometryBox = geometry.bbox
      ? bboxPolygon(geometry.bbox)
      : bboxPolygon(bbox(geometry))

    // Run an initial pre-filter against the geometry's BoundingBox (speeds up
    // second run with a much smaller set of markups to check against the more
    // complex geometry)
    // Also filter out any shared markups
    const markupsInBox = markups.filter((markup) => {
      if (!includeShared && !!markup.owner) {
        return false
      }

      return booleanIntersects(geometryBox, markup.toGeoJSON()?.geometry)
    })

    // Run second filter against the more complex geometry
    return markupsInBox.filter((markup) =>
      booleanIntersects(geometry, markup.toGeoJSON()?.geometry)
    )
  }

  /**
   * Handle track trimming functionality with paywall check
   * @param lineMarkup - Line markup object to trim
   * @param context - Context that the track trim is being launched from ('card', 'ellipsis', etc.)
   */
  trimTrack(lineMarkup: Line | Track, context: string = 'card') {
    if (!Paywall.checkAccess()) {
      Paywall.displayPayWallMessage('trimTrack')
      return
    }

    panToMarkup(lineMarkup)

    router.push({
      name: `fish-trim-track-card`,
      params: {
        id: lineMarkup.uuid,
      },
      state: { context },
    })
  }
}

const markupService = new MarkupService()

export default reactive(markupService)
